/*
 * <home-product-carousel>
 * Lightweight, dependency-free image carousel for the home page.
 * - Auto-scrolls left (configurable via data-autoplay / data-autoplay-speed)
 * - Desktop: white-bar dots; Mobile: touch swipe
 * - Seamless infinite loop: the first/last slides are cloned so going from the
 *   last slide to the first keeps sliding in the same direction (no "rewind"
 *   back across the middle slides). After landing on a clone we snap (without
 *   animation) to the matching real slide on transitionend.
 * - Multiple instances are independent; connected/disconnected callbacks make it
 *   Shopify theme-editor safe (re-init on section reload, timers cleaned up).
 *
 * Track layout after buildLoop():
 *   [ lastClone, real0, real1, …, real(N-1), firstClone ]
 *      pos 0     pos 1                pos N      pos N+1
 * The first real slide sits at extended position 1.
 */
class HomeProductCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.home-product-carousel__track');
    this.dots = Array.from(this.querySelectorAll('.home-product-carousel__dot'));
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.speed = Math.max(2000, Number(this.dataset.autoplaySpeed) || 4000);
    this.timer = null;
    this.startX = null;
    this.deltaX = 0;

    const realSlides = this.track ? Array.from(this.track.children) : [];
    this.realCount = realSlides.length;

    // A single (or empty) slide is just a static image — nothing to loop.
    if (!this.track || this.realCount <= 1) return;

    this.buildLoop(realSlides);

    this.onDotClick = this.onDotClick.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.onVisibility = this.onVisibility.bind(this);
    this.onPointerEnter = this.stop.bind(this);
    this.onPointerLeave = this.restart.bind(this);

    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.track.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.track.addEventListener('touchmove', this.onTouchMove, { passive: true });
    this.track.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.track.addEventListener('transitionend', this.onTransitionEnd);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);

    // Park on the first real slide (extended position 1) without animating.
    this.setPosition(1, false);
    this.updateDots();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.track?.removeEventListener('touchstart', this.onTouchStart);
    this.track?.removeEventListener('touchmove', this.onTouchMove);
    this.track?.removeEventListener('touchend', this.onTouchEnd);
    this.track?.removeEventListener('transitionend', this.onTransitionEnd);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
  }

  // Clone the first/last real slide so the track can wrap around seamlessly.
  buildLoop(realSlides) {
    const firstClone = realSlides[0].cloneNode(true);
    const lastClone = realSlides[this.realCount - 1].cloneNode(true);

    [firstClone, lastClone].forEach((clone) => {
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.clone = 'true';
      clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
    });

    this.track.appendChild(firstClone);
    this.track.insertBefore(lastClone, realSlides[0]);
  }

  onDotClick(event) {
    const realIndex = this.dots.indexOf(event.currentTarget);
    if (realIndex < 0) return;
    this.normalize();
    this.setPosition(realIndex + 1, true);
    this.updateDots();
    this.restart();
  }

  onTouchStart(event) {
    this.startX = event.touches[0].clientX;
    this.deltaX = 0;
  }

  onTouchMove(event) {
    if (this.startX === null) return;
    this.deltaX = event.touches[0].clientX - this.startX;
  }

  onTouchEnd() {
    if (this.startX === null) return;
    const delta = this.deltaX;
    this.startX = null;
    this.deltaX = 0;

    if (Math.abs(delta) >= 40) {
      if (delta < 0) this.next();
      else this.prev();
    }
    // Always resume autoplay after a touch, even on a tap that didn't change slide.
    this.restart();
  }

  onVisibility() {
    if (document.hidden) this.stop();
    else this.restart();
  }

  // When a transition onto a clone finishes, jump (no animation) to the matching
  // real slide so the next move continues seamlessly in the same direction.
  onTransitionEnd(event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    if (this.position === this.realCount + 1) {
      this.setPosition(1, false);
    } else if (this.position === 0) {
      this.setPosition(this.realCount, false);
    }
  }

  // If we're parked on a clone (e.g. a new move started before transitionend),
  // snap to the equivalent real slide first so we never run past the clones.
  normalize() {
    if (this.position === this.realCount + 1) this.setPosition(1, false);
    else if (this.position === 0) this.setPosition(this.realCount, false);
  }

  next() {
    this.normalize();
    this.setPosition(this.position + 1, true);
    this.updateDots();
  }

  prev() {
    this.normalize();
    this.setPosition(this.position - 1, true);
    this.updateDots();
  }

  setPosition(position, animate) {
    this.position = position;

    if (animate) {
      this.track.style.transform = `translateX(-${position * 100}%)`;
    } else {
      const previousTransition = this.track.style.transition;
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(-${position * 100}%)`;
      // Force reflow so the snap is applied instantly (no animation) and the
      // following animated move starts cleanly from here.
      void this.track.offsetWidth;
      this.track.style.transition = previousTransition;
    }
  }

  updateDots() {
    const realIndex = (((this.position - 1) % this.realCount) + this.realCount) % this.realCount;
    this.dots.forEach((dot, dotIndex) => {
      dot.classList.toggle('is-active', dotIndex === realIndex);
    });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  start() {
    this.stop();
    if (!this.autoplayEnabled || this.prefersReducedMotion()) return;
    this.timer = window.setInterval(() => this.next(), this.speed);
  }

  stop() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  restart() {
    if (!this.autoplayEnabled || document.hidden) return;
    this.start();
  }
}

if (!customElements.get('home-product-carousel')) {
  customElements.define('home-product-carousel', HomeProductCarousel);
}
